import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Facebook, MessageCircle, Twitter, Linkedin, Link2, Download, Share2, Sparkles, RefreshCw, Volume2, Pause } from "lucide-react";
import { toast } from "sonner";
import { absoluteUrl, adminHeaders } from "../lib/api";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Verset() {
  const { t } = useTranslation();
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);

  const load = async (date) => {
    setLoading(true);
    try {
      const url = date ? `${API}/votd?date=${date}` : `${API}/votd`;
      const { data } = await axios.get(url);
      setVerse(data);
    } catch (e) {
      toast.error(T("Impossible de charger le verset du jour."));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const imageUrl = verse ? absoluteUrl(`${verse.share_url}`) : "";
  const shareText = verse ? `${verse.text} — ${T("Sourate")} ${verse.sourate_num} (${verse.sourate_name_ar_translit}) v.${verse.verset_num}` : "";
  const pageUrl = "https://papicoo.com/verset";

  const shareLinks = verse ? {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}&quote=${encodeURIComponent(shareText)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + " — " + pageUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`,
  } : {};

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${shareText} — ${pageUrl}`); toast.success(T("Verset copié.")); }
    catch { toast.error(T("Copie impossible.")); }
  };

  const downloadImage = async () => {
    if (!imageUrl) return;
    try {
      const r = await fetch(imageUrl);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `papicoo-verset-${verse.date}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(T("Téléchargement impossible.")); }
  };

  return (
    <div data-testid="page-verset" className="max-w-5xl mx-auto px-4 sm:px-8 pt-10 pb-32">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{t("verset.eyebrow")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {t("verset.title1")}<br />
        <span className="italic font-light">{t("verset.title2")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl">
        {t("verset.intro")}
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr_1fr] gap-6 items-start">
        {/* Image carrée */}
        <div className="papicoo-card overflow-hidden">
          {loading ? (
            <div className="aspect-square flex items-center justify-center"><Sparkles className="animate-pulse text-[color:var(--papicoo-muted)]" /></div>
          ) : verse ? (
            <img
              data-testid="votd-image"
              src={imageUrl}
              alt={T("Verset du {d}").replace("{d}", verse.date)}
              className="w-full h-auto block"
            />
          ) : null}
        </div>

        {/* Texte + actions */}
        <div className="papicoo-card p-6 lg:p-7">
          {verse && (
            <>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)]">
                {new Date(verse.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
              {verse.text_ar && (
                <p data-testid="votd-arabic" lang="ar" dir="rtl" className="papicoo-quran mt-4 text-[color:var(--papicoo-ink)]">
                  {verse.text_ar}
                </p>
              )}
              <div className="mt-5 text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Traduction française du sens")}</div>
              <p data-testid="votd-text" className="text-lg sm:text-xl text-[color:var(--papicoo-ink)] leading-relaxed mt-2">
                « {verse.text} »
              </p>
              <div data-testid="votd-reference" className="mt-4 text-sm text-[color:var(--papicoo-gold)] font-medium">
                {T("Sourate")} {verse.sourate_num} · {verse.sourate_name_ar_translit}
                <span className="text-[color:var(--papicoo-muted)] font-normal"> · {verse.sourate_name_fr}</span>
                <span className="text-[color:var(--papicoo-muted)]"> · {T("verset")} {verse.verset_num}</span>
              </div>
              {verse.arabic_source && <div className="mt-1 text-[11px] text-[color:var(--papicoo-muted)]">{T("Texte arabe : Tanzil Project (tanzil.net)")}</div>}

              <div className="mt-6 border-t border-[color:var(--papicoo-border)] pt-5">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] mb-3 inline-flex items-center gap-1">
                  <Share2 size={12} /> {T("Partage cette baraka")}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <a data-testid="share-fb" href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-105 transition" aria-label={T("Facebook")}><Facebook size={18} /></a>
                  <a data-testid="share-wa" href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-105 transition" aria-label={T("WhatsApp")}><MessageCircle size={18} /></a>
                  <a data-testid="share-tw" href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#0F0F0F] text-white hover:scale-105 transition" aria-label={T("X / Twitter")}><Twitter size={18} /></a>
                  <a data-testid="share-li" href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#0A66C2] text-white hover:scale-105 transition" aria-label={T("LinkedIn")}><Linkedin size={18} /></a>
                  <button data-testid="share-copy" onClick={copyLink} className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] hover:scale-105 transition" aria-label={T("Copier")}><Link2 size={18} /></button>
                </div>

                <button
                  data-testid="votd-download"
                  onClick={downloadImage}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full papicoo-btn-gold text-sm"
                >
                  <Download size={14} /> {t("verset.download")}
                </button>

                <button
                  data-testid="votd-listen"
                  onClick={async () => {
                    if (audioState === "playing") { audioRef.current?.pause(); setAudioState("idle"); return; }
                    setAudioState("loading");
                    try {
                      const res = await axios.get(absoluteUrl(`/api/votd/audio${verse.date ? `?date=${verse.date}` : ""}`), { responseType: "blob", headers: adminHeaders() });
                      const url = URL.createObjectURL(res.data);
                      audioRef.current?.pause();
                      const a = new Audio(url);
                      audioRef.current = a;
                      a.onended = () => setAudioState("idle");
                      a.onerror = () => { setAudioState("idle"); toast.error(T("Lecture impossible.")); };
                      await a.play();
                      setAudioState("playing");
                    } catch (e) { setAudioState("idle"); if (e?.response?.status === 401) toast.info(T("La lecture vocale est réservée pour le moment. Le texte reste disponible.")); else toast.error(T("Lecture impossible.")); }
                  }}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] hover:bg-[color:var(--papicoo-accent)] transition text-sm"
                >
                  {audioState === "loading" ? <Sparkles size={14} className="animate-pulse" /> : audioState === "playing" ? <Pause size={14} /> : <Volume2 size={14} />}
                  {audioState === "loading" ? t("verset.preparing") : audioState === "playing" ? t("verset.listening") : t("verset.listen")}
                </button>
              </div>
              <div className="mt-4 text-[11px] italic text-[color:var(--papicoo-muted)] text-center">
                {t("verset.tomorrow")}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
